import React from 'react';
import * as R from 'ramda';
import connectToI18n from 'modules/i18n/connectToI18n';
import { validate, validators } from 'modules/validation';
import PropTypes from 'prop-types';
import { Button, Heading, LoadingAnimation } from 'components/ui';
import { AddressInputGroup, FormSelectGroup, TextInputGroup } from 'components/ui/form';
import { connect } from 'react-redux';
import {
    editInvoiceForm,
    redirectToPaymentPage,
    requestInvoice,
    selectInvoiceForm,
    selectInvoiceFormReady,
    selectLockedForm,
    unlockForms,
    fetchCompany,
} from 'modules/giftCard';
import Backbone from 'backbone';
import { userSelector } from 'modules/currentUser';

export class BuyGiftCardInvoice extends React.Component {
    static propTypes = {
        editInvoiceForm: PropTypes.func.isRequired,
        formData: PropTypes.object.isRequired,
        isPaymentPageBeingCreated: PropTypes.bool,
        isReady: PropTypes.bool,
        unlockForms: PropTypes.func.isRequired,
        redirectToPaymentPage: PropTypes.func.isRequired,
        requestInvoice: PropTypes.func.isRequired,
        trans: PropTypes.func.isRequired,
        fetchCompany: PropTypes.func.isRequired,
        user: PropTypes.object.isRequired,
    };

    state = {
        clientErrors: {},
    };

    componentDidMount() {
        if (!this.props.user.anonymous) {
            this.props.fetchCompany(this.props.user.id);
        }
    }

    onBack = () => {
        this.props.unlockForms();
        Backbone.history.navigate('giftcards', { trigger: true });
    };

    onChange = (name, value) => {
        this.props.editInvoiceForm(name, value);
        this.setState(prevState => {
            const clientErrors = prevState.clientErrors;
            clientErrors[name] = [];
            return {
                ...prevState,
                clientErrors,
            };
        });
    };

    onSubmit = () => {
        const clientErrors = this.validate(this.props.formData);

        if (!R.isEmpty(clientErrors)) {
            this.setState({
                clientErrors,
            });
        } else {
            this.props.requestInvoice(true);
            this.props.redirectToPaymentPage();
        }
    };

    handleZipChange = onZipChange => (name, value) => {
        this.onChange(name, value);
        onZipChange(name, value);
    };

    handleCountryChange = onChangeCountry => (name, value) => {
        this.onChange(name, value);
        onChangeCountry(name, value);
    };

    validate = formData => {
        const { assertNotBlank } = validators(this.props.trans);
        const ret = validate.all([
            validate.prop('companyName', assertNotBlank),
            validate.prop('street', assertNotBlank),
            validate.prop('cityId', assertNotBlank),
        ])(formData);

        const [, clientErrors] = ret;

        return clientErrors;
    };

    render() {
        const { formData, isPaymentPageBeingCreated, isReady, trans } = this.props;

        const { clientErrors } = this.state;

        if (!isReady) {
            return <LoadingAnimation type="spinner" />;
        }

        return (
            <div className="buyGiftCardMainContent">
                <Heading size={2} className="u-mb-4" responsive>
                    {trans('buyGiftCard.invoiceForm.title')}
                </Heading>
                <div>
                    <TextInputGroup
                        clientErrors={clientErrors.companyName}
                        inputVariant="inverse"
                        label={trans('companies.form.name')}
                        name="companyName"
                        onChange={this.onChange}
                        showErrors={!R.isEmpty(clientErrors.companyName)}
                        value={formData.companyName}
                    />
                    <AddressInputGroup
                        cityName="cityId"
                        initialCountry={formData._country}
                        initialZip={formData._zip}
                        onChange={this.onChange}
                    >
                        {({
                            countries,
                            cities,
                            country,
                            zip,
                            onCountryChange,
                            onZipChange,
                            areCitiesReady,
                        }) => (
                            <React.Fragment>
                                <TextInputGroup
                                    className="paymentProfileCompletion-formCell"
                                    name="street"
                                    label={trans('address.numberWay')}
                                    value={formData.street}
                                    onChange={this.onChange}
                                    inputVariant="inverse"
                                    showErrors={!R.isEmpty(clientErrors.street)}
                                    clientErrors={clientErrors.street}
                                />
                                <div className="paymentProfileCompletion-formCell u-flexible-row">
                                    <TextInputGroup
                                        className="paymentProfileCompletion-zipCode"
                                        name="_zip"
                                        inputVariant="inverse"
                                        label={trans('address.zip')}
                                        value={zip}
                                        onChange={this.handleZipChange(onZipChange)}
                                    />
                                    <FormSelectGroup
                                        className="paymentProfileCompletion-city"
                                        name="cityId"
                                        clientErrors={clientErrors.cityId}
                                        showErrors={!R.isEmpty(clientErrors.cityId)}
                                        isDisabled={!areCitiesReady}
                                        emptyOption={
                                            cities.length !== 0
                                                ? trans('address.cityDefault')
                                                : trans('user.city.void')
                                        }
                                        inputVariant="inverse"
                                        label={trans('address.city')}
                                        options={cities}
                                        value={formData.cityId.toString()}
                                        onChange={this.onChange}
                                    />
                                </div>
                                <FormSelectGroup
                                    className="paymentProfileCompletion-formCell"
                                    name="_country"
                                    emptyOption={trans('address.countryChoose')}
                                    inputVariant="inverse"
                                    label={trans('address.country')}
                                    options={countries}
                                    value={country}
                                    onChange={this.handleCountryChange(onCountryChange)}
                                />
                            </React.Fragment>
                        )}
                    </AddressInputGroup>
                    <TextInputGroup
                        inputVariant="inverse"
                        label={trans('global.label.siretNumber')}
                        name="companyNumber"
                        onChange={this.onChange}
                        value={formData.companyNumber}
                    />
                    <TextInputGroup
                        inputVariant="inverse"
                        label={trans('companies.form.VatNumber')}
                        name="vatNumber"
                        onChange={this.onChange}
                        value={formData.vatNumber}
                    />
                    <Button
                        block
                        loading={isPaymentPageBeingCreated}
                        onClick={this.onSubmit}
                        variant="success"
                    >
                        {trans('global.continue')}
                    </Button>
                </div>
                <Button icon="angle-left" weight="link" onClick={this.onBack}>
                    {trans('global.back')}
                </Button>
            </div>
        );
    }
}

export default connect(
    state => ({
        formData: selectInvoiceForm(state),
        isPaymentPageBeingCreated: selectLockedForm(state),
        isReady: selectInvoiceFormReady(state),
        user: userSelector(state),
    }),
    {
        editInvoiceForm,
        redirectToPaymentPage,
        requestInvoice,
        unlockForms,
        fetchCompany,
    }
)(connectToI18n(BuyGiftCardInvoice));
